import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Search, Plus, Heart, ExternalLink, Trash2, Image, Youtube, Lock, Upload, FileText, Download, MessageCircle, Send, X, Bookmark, Star, Pencil, Hammer, Camera, Flame, FolderPlus, ChevronRight, Share2, Users, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== CHALLENGES TAB ====================
const ChallengesTab = ({ challenges, user, isDemo, canPost, navigate, fetchChallenges }) => {
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [detail, setDetail] = useState(null);
  const [myIdeas, setMyIdeas] = useState([]);
  const [selectedIdeaId, setSelectedIdeaId] = useState('');
  const [entering, setEntering] = useState(false);
  const [pastWinners, setPastWinners] = useState([]);
  const [showPast, setShowPast] = useState(false);

  const fetchDetail = async (chId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/diy-challenges/${chId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setDetail(res.data);
    } catch { /* empty */ }
  };

  const fetchMyIdeas = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/diy-ideas?bookmarks_only=false`, { headers: { Authorization: `Bearer ${token}` } });
      setMyIdeas((res.data || []).filter(i => i.user_id === user?.id));
    } catch { /* empty */ }
  };

  const fetchPastWinners = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/diy-challenges/past-winners`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setPastWinners(res.data || []);
    } catch { /* empty */ }
    setShowPast(true);
  };

  const enterChallenge = async (challengeId) => {
    if (!selectedIdeaId) { toast.error('Select an idea to enter'); return; }
    setEntering(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/diy-challenges/${challengeId}/enter`, { idea_id: selectedIdeaId }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Entry submitted!');
      fetchDetail(challengeId);
      setSelectedIdeaId('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setEntering(false);
  };

  const voteEntry = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/diy-challenges/entries/${entryId}/vote`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (detail) fetchDetail(detail.id);
    } catch { /* empty */ }
  };

  const getPhotoPath = (p) => typeof p === 'string' ? p : p?.path || '';

  if (showPast) {
    return (
      <div className="space-y-4">
        <button onClick={() => setShowPast(false)} className="text-sm text-amber-600 font-medium hover:underline flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back to Challenges</button>
        <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2"><Star className="h-5 w-5 text-amber-500 fill-amber-500" /> Past Challenge Winners</h2>
        {pastWinners.length === 0 ? <p className="text-sm text-stone-400">No winners yet</p> : pastWinners.map(ch => (
          <div key={ch.id} className="bg-white rounded-2xl border border-amber-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-stone-800">{ch.title}</h3>
              <span className="text-xs text-stone-400">{ch.month}/{ch.year}</span>
            </div>
            <div className="flex items-center gap-3 mt-2 bg-amber-50 rounded-xl p-3">
              <Star className="h-6 w-6 text-amber-500 fill-amber-500" />
              <div>
                <div className="font-semibold text-amber-800">{ch.winner_name}</div>
                {ch.winner_idea && <div className="text-sm text-stone-600">{ch.winner_idea.title}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (detail) {
    const myEntryCount = (detail.entries || []).filter(e => e.user_id === user?.id).length;
    const canEnterMore = myEntryCount < 3;
    const daysLeft = detail.end_date ? Math.max(0, Math.ceil((new Date(detail.end_date) - new Date()) / 86400000)) : null;
    return (
      <div className="space-y-4">
        <button onClick={() => { setDetail(null); fetchChallenges(); }} className="text-sm text-amber-600 font-medium hover:underline flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back</button>
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-stone-800">{detail.title}</h2>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${detail.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'}`}>{detail.status}</span>
          </div>
          {detail.description && <p className="text-sm text-stone-600 mb-3">{detail.description}</p>}
          <div className="flex gap-4 text-xs text-stone-400">
            <span>{detail.entry_count || 0} entries</span>
            {daysLeft !== null && detail.status === 'active' && <span className="text-orange-600 font-medium">{daysLeft} days left</span>}
          </div>
        </div>

        {/* Entry Form */}
        {detail.status === 'active' && canEnterMore && !isDemo && canPost && (
          <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4 space-y-3">
            <h3 className="font-semibold text-amber-800 text-sm">Enter This Challenge ({myEntryCount}/3 entries used)</h3>
            <p className="text-xs text-amber-700">Nominate up to 3 of your ideas. Select one or create a new idea first.</p>
            <div className="flex gap-2">
              <Select value={selectedIdeaId || 'pick'} onValueChange={v => setSelectedIdeaId(v === 'pick' ? '' : v)} onOpenChange={() => { if (myIdeas.length === 0) fetchMyIdeas(); }}>
                <SelectTrigger className="flex-1 h-10 text-sm bg-white"><SelectValue placeholder="Choose your idea..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pick" disabled>Choose your idea...</SelectItem>
                  {myIdeas.map(i => <SelectItem key={i.id} value={i.id}>{i.title}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={() => enterChallenge(detail.id)} disabled={entering || !selectedIdeaId} className="bg-amber-600 hover:bg-amber-700 text-white rounded-full h-10">{entering ? '...' : 'Submit Entry'}</Button>
            </div>
          </div>
        )}
        {myEntryCount > 0 && !canEnterMore && <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700 font-medium">You've submitted all 3 entries!</div>}
        {myEntryCount > 0 && canEnterMore && <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-700 font-medium">You've entered {myEntryCount}/3 — you can add {3 - myEntryCount} more!</div>}

        {/* Leaderboard */}
        <div>
          <h3 className="font-bold text-stone-800 mb-3">Leaderboard</h3>
          {(detail.entries || []).length === 0 ? <p className="text-sm text-stone-400">No entries yet — be the first!</p> : (
            <div className="space-y-2">
              {(detail.entries || []).map((entry, idx) => (
                <div key={entry.id} className="bg-white rounded-xl border border-stone-200 p-3 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${idx === 0 ? 'bg-amber-500 text-white' : idx === 1 ? 'bg-stone-400 text-white' : idx === 2 ? 'bg-amber-700 text-white' : 'bg-stone-100 text-stone-600'}`}>
                    {idx + 1}
                  </div>
                  {entry.idea?.photos?.[0] && <img src={`${API}/files/${getPhotoPath(entry.idea.photos[0])}`} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                  <div className="flex-1 cursor-pointer hover:bg-stone-50 rounded-lg p-1 transition" onClick={() => navigate(`/?view=idea-detail&ideaId=${entry.idea_id}`)}>
                    <div className="font-semibold text-sm text-stone-800 hover:text-amber-700">{entry.idea?.title || 'Untitled'}</div>
                    <div className="text-xs text-stone-500">by <span className="text-amber-600 cursor-pointer" onClick={(e) => { e.stopPropagation(); navigate(`/builder/${entry.user_id}`); }}>{entry.author_name}</span></div>
                    {entry.idea?.description && <p className="text-xs text-stone-400 mt-0.5 line-clamp-1">{entry.idea.description}</p>}
                  </div>
                  <button onClick={() => !isDemo && voteEntry(entry.id)} className={`flex items-center gap-1 text-sm px-3 py-1.5 rounded-full transition font-medium ${(entry.voted_by || []).includes(user?.id) ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600 hover:bg-amber-50'}`}>
                    <Heart className={`h-4 w-4 ${(entry.voted_by || []).includes(user?.id) ? 'fill-current' : ''}`} /> {entry.votes || 0}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-stone-800">Monthly Challenges</h2>
        <button onClick={fetchPastWinners} className="text-xs text-amber-600 font-medium hover:underline flex items-center gap-1">
          <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> Past Winners
        </button>
      </div>
      {challenges.length === 0 ? (
        <div className="text-center py-12 text-stone-400"><p className="text-sm">No challenges yet — check back soon!</p></div>
      ) : challenges.map(ch => (
        <div key={ch.id} onClick={() => { setSelectedChallenge(ch.id); fetchDetail(ch.id); }} className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5 hover:shadow-md transition cursor-pointer">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-stone-800">{ch.title}</h3>
            <div className="flex items-center gap-2">
              {ch.challenge_type === 'bonus' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">Bonus</span>}
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ch.status === 'active' ? 'bg-green-100 text-green-700' : ch.status === 'scheduled' ? 'bg-blue-100 text-blue-700' : 'bg-stone-100 text-stone-600'}`}>{ch.status}</span>
            </div>
          </div>
          {ch.description && <p className="text-sm text-stone-600 mb-2">{ch.description}</p>}
          <div className="flex items-center gap-4 text-xs text-stone-400">
            <span>{ch.entry_count || 0} entries</span>
            {ch.end_date && <span>Ends: {ch.end_date.split('T')[0]}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

const CATEGORIES = [
  { value: 'hive_plans', label: 'Hive Plans' },
  { value: 'apiary_layouts', label: 'Apiary Layouts' },
  { value: 'honey_house', label: 'Honey House' },
  { value: 'equipment_diy', label: 'Equipment DIY' },
  { value: 'swarm_traps', label: 'Swarm Traps' },
  { value: 'frames_foundation', label: 'Frames & Foundation' },
  { value: 'extraction', label: 'Extraction & Processing' },
  { value: 'feeding', label: 'Feeding & Nutrition' },
  { value: 'pest_management', label: 'Pest Management' },
  { value: 'transport', label: 'Transport & Moving' },
  { value: 'protective_gear', label: 'Protective Gear' },
  { value: 'garden_pollination', label: 'Garden & Pollination' },
  { value: 'tech_gadgets', label: 'Tech & Gadgets' },
  { value: 'other', label: 'Other' },
  { value: 'general', label: 'General' },
];
const CAT_COLORS = {
  hive_plans: 'bg-amber-100 text-amber-700 border-amber-200',
  apiary_layouts: 'bg-green-100 text-green-700 border-green-200',
  honey_house: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  equipment_diy: 'bg-blue-100 text-blue-700 border-blue-200',
  swarm_traps: 'bg-purple-100 text-purple-700 border-purple-200',
  frames_foundation: 'bg-orange-100 text-orange-700 border-orange-200',
  extraction: 'bg-rose-100 text-rose-700 border-rose-200',
  feeding: 'bg-lime-100 text-lime-700 border-lime-200',
  pest_management: 'bg-red-100 text-red-700 border-red-200',
  transport: 'bg-sky-100 text-sky-700 border-sky-200',
  protective_gear: 'bg-slate-100 text-slate-700 border-slate-200',
  garden_pollination: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  tech_gadgets: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  other: 'bg-stone-100 text-stone-600 border-stone-200',
  general: 'bg-stone-100 text-stone-700 border-stone-200',
};
const DIFF_LABELS = { easy: 'Easy', moderate: 'Moderate', advanced: 'Advanced', expert: 'Expert' };
const DIFF_COLORS = { easy: 'bg-green-100 text-green-700', moderate: 'bg-amber-100 text-amber-700', advanced: 'bg-orange-100 text-orange-700', expert: 'bg-red-100 text-red-700' };
const COST_LABELS = { '$': '$', '$$': '$$', '$$$': '$$$', '$$$$': '$$$$' };
const TIME_LABELS = { '<1hr': '< 1 Hour', half_day: 'Half Day', full_day: 'Full Day', weekend: 'Weekend', multi_day: 'Multi-Day' };

const Stars = ({ count, size = 'h-3.5 w-3.5' }) => (
  <span className="flex">{[1,2,3,4,5].map(i => <Star key={i} className={`${size} ${i <= count ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`} />)}</span>
);

const StarInput = ({ value, onChange }) => (
  <div className="flex gap-1">{[1,2,3,4,5].map(i => (
    <button key={i} type="button" onClick={() => onChange(i)} className="p-0.5">
      <Star className={`h-5 w-5 transition ${i <= value ? 'text-amber-500 fill-amber-500' : 'text-stone-300 hover:text-amber-400'}`} />
    </button>
  ))}</div>
);

// ==================== COMMENTS ====================
const CommentSection = ({ ideaId, isDemo }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/diy-ideas/${ideaId}/comments`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setComments(res.data || []);
    } catch { /* empty */ }
  }, [ideaId]);

  useEffect(() => { if (expanded) fetchComments(); }, [expanded, fetchComments]);

  const postComment = async () => {
    if (!text.trim() || isDemo) return;
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/diy-ideas/${ideaId}/comments`, { text: text.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => [...prev, res.data]);
      setText('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to post'); }
    setSending(false);
  };

  const deleteComment = async (cid) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/diy-ideas/comments/${cid}`, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => prev.filter(c => c.id !== cid));
    } catch { /* empty */ }
  };

  return (
    <div className="pt-2 border-t border-stone-100 mt-2">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700">
        <MessageCircle className="h-3.5 w-3.5" /> {expanded ? 'Hide' : 'Comments'} {comments.length > 0 && `(${comments.length})`}
      </button>
      {expanded && (
        <div className="mt-2 space-y-2">
          {comments.map(c => (
            <div key={c.id} className="bg-stone-50 rounded-lg px-3 py-2 text-xs group">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-stone-700">{c.author_name || 'Anonymous'}</span>
                <div className="flex items-center gap-1">
                  <span className="text-stone-400">{(c.created_at || '').split('T')[0]}</span>
                  {c.user_id === user?.id && <button onClick={() => deleteComment(c.id)} className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>}
                </div>
              </div>
              <p className="text-stone-600 mt-0.5 whitespace-pre-wrap">{c.text}</p>
            </div>
          ))}
          {!isDemo && (
            <div className="flex gap-2">
              <Input value={text} onChange={e => setText(e.target.value)} placeholder="Ask a question or share a tip..." className="h-8 text-xs flex-1" onKeyDown={e => e.key === 'Enter' && postComment()} />
              <button onClick={postComment} disabled={sending || !text.trim()} className="h-8 px-3 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1">
                <Send className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== MADE IT! SECTION ====================
const MadeItSection = ({ ideaId, isDemo }) => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState('');
  const [stars, setStarsVal] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const fetchPosts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/diy-ideas/${ideaId}/made-it`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setPosts(res.data || []);
    } catch { /* empty */ }
  }, [ideaId]);

  useEffect(() => { if (expanded) fetchPosts(); }, [expanded, fetchPosts]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) { toast.error('Max 5 photos'); return; }
    setUploading(true);
    for (const file of files) {
      try {
        const token = localStorage.getItem('token');
        const fd = new FormData(); fd.append('file', file);
        const res = await axios.post(`${API}/diy-ideas/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
        setPhotos(prev => [...prev, { path: res.data.path, filename: res.data.filename }]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setUploading(false);
    e.target.value = '';
  };

  const submitMadeIt = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/diy-ideas/${ideaId}/made-it`, { photos: photos.map(p => ({ path: p.path, filename: p.filename })), caption, stars }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Your build has been shared!');
      setShowAdd(false); setPhotos([]); setCaption(''); setStarsVal(0);
      fetchPosts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to post'); }
    setSubmitting(false);
  };

  return (
    <div className="pt-2 border-t border-stone-100">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-stone-500 hover:text-amber-700 font-medium">
        <Hammer className="h-3.5 w-3.5" /> Made It! {posts.length > 0 && `(${posts.length})`}
      </button>
      {expanded && (
        <div className="mt-2 space-y-2">
          {posts.map(p => (
            <div key={p.id} className="bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-amber-800">{p.author_name || 'Anonymous'}</span>
                {p.stars > 0 && <Stars count={p.stars} size="h-3 w-3" />}
              </div>
              {p.caption && <p className="text-xs text-stone-600 mt-1">{p.caption}</p>}
              {p.photos?.length > 0 && (
                <div className="flex gap-1.5 mt-2 overflow-x-auto">
                  {p.photos.map((ph, i) => <img key={i} src={`${API}/files/${typeof ph === 'string' ? ph : ph?.path || ''}`} alt="" className="h-16 w-16 object-cover rounded-lg flex-shrink-0 cursor-pointer hover:opacity-80 transition" onClick={() => setLightboxPhoto(`${API}/files/${typeof ph === 'string' ? ph : ph?.path || ''}`)} />)}
                </div>
              )}
            </div>
          ))}
          {!isDemo && !showAdd && (
            <button onClick={() => setShowAdd(true)} className="flex items-center gap-1 text-xs text-amber-600 font-medium hover:text-amber-700">
              <Camera className="h-3.5 w-3.5" /> Share your build
            </button>
          )}
          {showAdd && (
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-200 space-y-2">
              <div className="text-xs font-semibold text-amber-800">Share Your Build</div>
              <div className="flex gap-2 items-center">
                <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" id={`made-it-upload-${ideaId}`} />
                <label htmlFor={`made-it-upload-${ideaId}`} className="cursor-pointer text-xs bg-amber-600 text-white px-3 py-1.5 rounded-full font-medium hover:bg-amber-700">
                  {uploading ? 'Uploading...' : `Upload Photos (${photos.length}/5)`}
                </label>
              </div>
              {photos.length > 0 && (
                <div className="flex gap-1.5">{photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={`${API}/files/${p.path}`} alt="" className="h-12 w-12 rounded object-cover" />
                    <button onClick={() => setPhotos(prev => prev.filter((_,idx) => idx !== i))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"><X className="h-2.5 w-2.5" /></button>
                  </div>
                ))}</div>
              )}
              <Input value={caption} onChange={e => setCaption(e.target.value)} placeholder="How did it go? Any tips?" className="h-8 text-xs" />
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-600">Rate it:</span>
                <StarInput value={stars} onChange={setStarsVal} />
              </div>
              <div className="flex gap-2">
                <button onClick={submitMadeIt} disabled={submitting} className="text-xs bg-amber-600 text-white px-4 py-1.5 rounded-full font-medium hover:bg-amber-700 disabled:opacity-50">{submitting ? '...' : 'Share'}</button>
                <button onClick={() => { setShowAdd(false); setPhotos([]); setCaption(''); setStarsVal(0); }} className="text-xs text-stone-500 hover:text-stone-700">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
      {lightboxPhoto && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setLightboxPhoto(null)} data-testid="madeit-lightbox">
          <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightboxPhoto(null)} className="absolute -top-10 right-0 text-white hover:text-stone-300"><X className="h-6 w-6" /></button>
            <img src={lightboxPhoto} alt="" className="w-full max-h-[80vh] object-contain rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== IDEA CARD ====================
const IdeaCard = ({ idea, currentUserId, onLike, onDelete, onEdit, isDemo, isBookmarked, onBookmark, onAddToCollection }) => {
  const navigate = useNavigate();
  const catLabel = CATEGORIES.find(c => c.value === idea.category)?.label || idea.category;
  const catColor = CAT_COLORS[idea.category] || CAT_COLORS.general;
  const isOwner = idea.user_id === currentUserId;
  const isLiked = (idea.liked_by || []).includes(currentUserId);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState(0);

  const ytId = ((idea.youtube_url || '').match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/) || [])[1];
  const photos = idea.photos || [];
  const planFile = idea.plan_file;

  // Handle photos as objects {path, caption} or plain strings
  const getPhotoPath = (p) => typeof p === 'string' ? p : p?.path || '';
  const getPhotoCaption = (p) => typeof p === 'object' ? p?.caption || '' : '';

  return (
    <>
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden hover:shadow-md transition group" data-testid={`idea-card-${idea.id}`}>
        {/* Thumbnail */}
        {photos.length > 0 ? (
          <div className="relative cursor-pointer" onClick={() => { setGalleryIdx(0); setShowGallery(true); }}>
            <img src={`${API}/files/${getPhotoPath(photos[0])}`} alt="" className="w-full h-44 object-cover" onError={e => { e.target.style.display = 'none'; }} />
            {photos.length > 1 && <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">+{photos.length - 1} more</div>}
          </div>
        ) : ytId ? (
          <a href={idea.youtube_url} target="_blank" rel="noopener noreferrer" className="block relative">
            <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt="" className="w-full h-44 object-cover" />
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"><Youtube className="h-10 w-10 text-white drop-shadow" /></div>
          </a>
        ) : (
          <div className="w-full h-24 bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center"><Image className="h-8 w-8 text-amber-300" /></div>
        )}

        <div className="p-4 space-y-2">
          {/* Title + Category */}
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-bold text-stone-800 text-sm leading-tight flex-1 cursor-pointer hover:text-amber-700" onClick={() => navigate(`/?view=idea-detail&ideaId=${idea.id}`)}>{idea.title}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${catColor}`}>{catLabel}</span>
          </div>

          {/* Builder Ratings Badges */}
          {(idea.difficulty || idea.cost_rating || idea.time_estimate) && (
            <div className="flex flex-wrap gap-1.5">
              {idea.difficulty && <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${DIFF_COLORS[idea.difficulty] || 'bg-stone-100 text-stone-600'}`}>{DIFF_LABELS[idea.difficulty] || idea.difficulty}</span>}
              {idea.cost_rating && <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700">{idea.cost_rating}</span>}
              {idea.time_estimate && <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">{TIME_LABELS[idea.time_estimate] || idea.time_estimate}</span>}
            </div>
          )}

          {/* Star Rating */}
          {idea.review_count > 0 && (
            <div className="flex items-center gap-1.5">
              <Stars count={Math.round(idea.review_avg)} size="h-3 w-3" />
              <span className="text-xs text-stone-500">{idea.review_avg} ({idea.review_count})</span>
              {idea.made_it_count > 0 && <span className="text-xs text-amber-600 ml-1">{idea.made_it_count} made it</span>}
            </div>
          )}

          {idea.description && <p className="text-xs text-stone-500 line-clamp-2">{idea.description}</p>}

          {idea.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">{idea.tags.slice(0, 4).map(t => <span key={t} className="text-[10px] bg-stone-50 text-stone-500 px-1.5 py-0.5 rounded">#{t}</span>)}</div>
          )}

          {/* Links */}
          <div className="flex items-center gap-3">
            {idea.youtube_url && <a href={idea.youtube_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"><Youtube className="h-3.5 w-3.5" /> Video <ExternalLink className="h-3 w-3" /></a>}
            {planFile && <a href={`${API}/files/${planFile.path}`} download={planFile.filename} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700" data-testid="download-plan"><Download className="h-3.5 w-3.5" /> Plan</a>}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-1 border-t border-stone-100">
            <div className="flex items-center gap-1.5">
              <button onClick={() => onLike(idea.id)} className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition ${isLiked ? 'bg-red-50 text-red-600' : 'text-stone-400 hover:text-red-500 hover:bg-red-50'}`}>
                <Heart className={`h-3.5 w-3.5 ${isLiked ? 'fill-current' : ''}`} /> {idea.likes || 0}
              </button>
              <button onClick={() => onBookmark(idea.id)} className={`p-1 rounded-full transition ${isBookmarked ? 'text-amber-600' : 'text-stone-400 hover:text-amber-500'}`} title={isBookmarked ? 'Unsave' : 'Save'}>
                <Bookmark className={`h-3.5 w-3.5 ${isBookmarked ? 'fill-current' : ''}`} />
              </button>
              {onAddToCollection && (
                <button onClick={() => onAddToCollection(idea.id)} className="text-stone-400 hover:text-indigo-500 p-1" title="Add to collection">
                  <FolderPlus className="h-3.5 w-3.5" />
                </button>
              )}
              {isOwner && <button onClick={() => onEdit(idea)} className="text-xs text-stone-400 hover:text-blue-500 p-1"><Pencil className="h-3.5 w-3.5" /></button>}
              {isOwner && <button onClick={() => onDelete(idea.id)} className="text-xs text-stone-400 hover:text-red-500 p-1"><Trash2 className="h-3.5 w-3.5" /></button>}
              {idea.allow_sharing && (
              <button onClick={() => {
                const url = `${window.location.origin}/diy-ideas/${idea.id}`;
                if (navigator.share) { navigator.share({ title: idea.title, text: idea.description?.slice(0, 100), url }); }
                else { navigator.clipboard.writeText(url); toast.success('Link copied!'); }
              }} className="text-stone-400 hover:text-blue-500 p-1" title="Share">
                <Share2 className="h-3.5 w-3.5" />
              </button>
              )}
            </div>
            <span className="text-[11px] text-amber-600 cursor-pointer hover:underline" onClick={() => navigate(`/?view=builder&userId=${idea.user_id}`)}>{idea.author_name || 'Anonymous'}</span>
          </div>

          {/* Made It! */}
          <MadeItSection ideaId={idea.id} isDemo={isDemo} />
          {/* Comments */}
          <CommentSection ideaId={idea.id} isDemo={isDemo} />
        </div>
      </div>

      {/* Gallery */}
      {showGallery && photos.length > 0 && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowGallery(false)}>
          <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowGallery(false)} className="absolute -top-10 right-0 text-white hover:text-stone-300"><X className="h-6 w-6" /></button>
            <img src={`${API}/files/${getPhotoPath(photos[galleryIdx])}`} alt="" className="w-full max-h-[70vh] object-contain rounded-xl" />
            {getPhotoCaption(photos[galleryIdx]) && (
              <div className="bg-black/60 text-white text-sm px-4 py-2 rounded-b-xl mt-0 text-center">{getPhotoCaption(photos[galleryIdx])}</div>
            )}
            {photos.length > 1 && (
              <>
                <div className="flex justify-center gap-2 mt-3">{photos.map((_, i) => <button key={i} onClick={() => setGalleryIdx(i)} className={`w-2.5 h-2.5 rounded-full transition ${i === galleryIdx ? 'bg-white' : 'bg-white/40'}`} />)}</div>
                <div className="flex justify-between mt-2">
                  <button onClick={() => setGalleryIdx(i => Math.max(0, i - 1))} disabled={galleryIdx === 0} className="text-white/70 hover:text-white disabled:opacity-30 text-sm px-3 py-1">Prev</button>
                  <span className="text-white/60 text-sm">{galleryIdx + 1} / {photos.length}</span>
                  <button onClick={() => setGalleryIdx(i => Math.min(photos.length - 1, i + 1))} disabled={galleryIdx === photos.length - 1} className="text-white/70 hover:text-white disabled:opacity-30 text-sm px-3 py-1">Next</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ==================== MAIN PAGE ====================
const DIYIdeas = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isDemo } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [savedOnly, setSavedOnly] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'general', youtube_url: '', tags: '', difficulty: '', cost_rating: '', time_estimate: '', allow_sharing: false });
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploadedPlan, setUploadedPlan] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [trending, setTrending] = useState([]);
  const [collections, setCollections] = useState([]);
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [newColDesc, setNewColDesc] = useState('');
  const [myCollections, setMyCollections] = useState([]);
  const [addToColIdea, setAddToColIdea] = useState(null);
  const [categoryBadges, setCategoryBadges] = useState({});
  const [challenges, setChallenges] = useState([]);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'ideas'); // ideas | challenges
  const [notifications, setNotifications] = useState([]);
  const [builderQuery, setBuilderQuery] = useState('');
  const [builderResults, setBuilderResults] = useState([]);
  const [showBuilderResults, setShowBuilderResults] = useState(false);

  const switchTab = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    if (tab === 'ideas') { params.delete('tab'); } else { params.set('tab', tab); }
    setSearchParams(params);
  };

  const searchBuildersRaw = useCallback(async (q) => {
    if (!q || q.length < 2) { setBuilderResults([]); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/builders/search?q=${encodeURIComponent(q)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setBuilderResults(res.data || []);
    } catch { setBuilderResults([]); }
  }, []);

  // Debounced builder search (300ms)
  const builderSearchTimer = React.useRef(null);
  const searchBuilders = useCallback((q) => {
    clearTimeout(builderSearchTimer.current);
    builderSearchTimer.current = setTimeout(() => searchBuildersRaw(q), 300);
  }, [searchBuildersRaw]);

  const canPost = user?.role === 'admin' || (user?.subscription_plan && !['free', 'trial'].includes(user.subscription_plan));

  const fetchIdeas = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      if (category && category !== 'all') params.set('category', category);
      if (search) params.set('search', search);
      if (sortBy) params.set('sort', sortBy);
      if (savedOnly) params.set('bookmarks_only', 'true');
      const res = await axios.get(`${API}/diy-ideas?${params}`, { headers });
      setIdeas(res.data || []);
    } catch { /* empty */ }
    setLoading(false);
  }, [category, search, sortBy, savedOnly]);

  const fetchBookmarks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/diy-ideas/user/bookmarks`, { headers: { Authorization: `Bearer ${token}` } });
      setBookmarkedIds(res.data || []);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { fetchIdeas(); }, [fetchIdeas]);
  useEffect(() => { fetchBookmarks(); }, [fetchBookmarks]);

  const fetchTrending = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/diy-ideas/trending/weekly`, { headers });
      setTrending(res.data || []);
    } catch { /* empty */ }
  }, []);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [pubRes, myRes] = await Promise.all([
          axios.get(`${API}/diy-collections`, { headers }),
          token ? axios.get(`${API}/diy-collections/mine`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve({ data: [] }),
        ]);
        setCollections(pubRes.data || []);
        setMyCollections(myRes.data || []);
      } catch { /* empty */ }
    };
    fetchTrending();
    fetchCollections();
  }, []);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/diy-ideas/category-counts`, { headers: { Authorization: `Bearer ${token}` } });
        setCategoryBadges(res.data || {});
      } catch { /* empty */ }
    };
    const fetchChallenges = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/diy-challenges`, { headers });
        setChallenges(res.data || []);
      } catch { /* empty */ }
    };
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/notifications/diy`, { headers: { Authorization: `Bearer ${token}` } });
        setNotifications(res.data || []);
      } catch { /* empty */ }
    };
    fetchBadges();
    fetchChallenges();
    fetchNotifications();
  }, []);

  const uploadFile = async (file) => {
    const token = localStorage.getItem('token');
    const fd = new FormData(); fd.append('file', file);
    const res = await axios.post(`${API}/diy-ideas/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
    return res.data;
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (uploadedPhotos.length + files.length > 10) { toast.error('Maximum 10 photos'); return; }
    setUploading(true);
    for (const file of files) {
      try {
        const result = await uploadFile(file);
        setUploadedPhotos(prev => [...prev, { path: result.path, filename: result.filename, caption: '' }]);
      } catch (err) { toast.error(`Failed: ${err.response?.data?.detail || err.message}`); }
    }
    setUploading(false);
    e.target.value = '';
  };

  const handlePlanUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const result = await uploadFile(file);
      setUploadedPlan({ path: result.path, filename: result.filename });
    } catch (err) { toast.error(`Failed: ${err.response?.data?.detail || err.message}`); }
    setUploading(false);
    e.target.value = '';
  };

  const openCreate = () => {
    setEditingIdea(null);
    setForm({ title: '', description: '', category: 'general', youtube_url: '', tags: '', difficulty: '', cost_rating: '', time_estimate: '', allow_sharing: false });
    setUploadedPhotos([]); setUploadedPlan(null);
    setShowCreate(true);
  };

  const openEdit = (idea) => {
    setEditingIdea(idea);
    setForm({
      title: idea.title || '', description: idea.description || '', category: idea.category || 'general',
      youtube_url: idea.youtube_url || '', tags: (idea.tags || []).join(', '),
      difficulty: idea.difficulty || '', cost_rating: idea.cost_rating || '', time_estimate: idea.time_estimate || '',
      allow_sharing: idea.allow_sharing || false,
    });
    setUploadedPhotos((idea.photos || []).map(p => typeof p === 'string' ? { path: p, filename: '', caption: '' } : { path: p.path, filename: p.filename || '', caption: p.caption || '' }));
    setUploadedPlan(idea.plan_file || null);
    setShowCreate(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
      const payload = { ...form, tags, photos: uploadedPhotos.map(p => ({ path: p.path, filename: p.filename, caption: p.caption })), plan_file: uploadedPlan, allow_sharing: form.allow_sharing };

      if (editingIdea) {
        await axios.put(`${API}/diy-ideas/${editingIdea.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Idea updated');
      } else {
        await axios.post(`${API}/diy-ideas`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Idea shared!');
      }
      setShowCreate(false);
      setEditingIdea(null);
      fetchIdeas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setCreating(false);
  };

  const handleLike = async (id) => {
    if (isDemo) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/diy-ideas/${id}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setIdeas(prev => prev.map(i => i.id !== id ? i : { ...i, likes: res.data.liked ? (i.likes||0)+1 : Math.max((i.likes||0)-1,0), liked_by: res.data.liked ? [...(i.liked_by||[]), user?.id] : (i.liked_by||[]).filter(x => x !== user?.id) }));
      fetchTrending();
    } catch { /* empty */ }
  };

  const handleBookmark = async (id) => {
    if (isDemo) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/diy-ideas/${id}/bookmark`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setBookmarkedIds(prev => res.data.bookmarked ? [...prev, id] : prev.filter(x => x !== id));
    } catch { /* empty */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this idea?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/diy-ideas/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setIdeas(prev => prev.filter(i => i.id !== id));
    } catch { toast.error('Failed to delete'); }
  };

  const createCollection = async () => {
    if (!newColName.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/diy-collections`, { name: newColName.trim(), description: newColDesc.trim(), is_public: true }, { headers: { Authorization: `Bearer ${token}` } });
      setMyCollections(prev => [res.data, ...prev]);
      setCollections(prev => [res.data, ...prev]);
      setNewColName(''); setNewColDesc(''); setShowNewCollection(false);
      toast.success('Collection created');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const addIdeaToCollection = async (collectionId) => {
    if (!addToColIdea) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/diy-collections/${collectionId}/ideas/${addToColIdea}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Added to collection');
      setAddToColIdea(null);
    } catch { toast.error('Failed to add'); }
  };

  return (
    <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <div className="max-w-7xl p-4 sm:p-6 pb-24">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2"><Lightbulb className="h-6 w-6 text-amber-600" /> DIY Ideas</h1>
            <p className="text-sm text-stone-500 mt-1">Share plans, layouts, and builds with the community</p>
          </div>
          {!isDemo && (
            <Button onClick={() => { if (!canPost) { toast('Requires a paid plan'); return; } openCreate(); }} className="rounded-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="share-idea-btn">
              <Plus className="h-4 w-4 mr-1" /> Share Idea {!canPost && <Lock className="h-3 w-3 ml-1" />}
            </Button>
          )}
        </div>

        {/* Builder Search */}
        <div className="relative mb-4">
          <div className="relative max-w-xs">
            <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              value={builderQuery}
              onChange={e => { setBuilderQuery(e.target.value); searchBuilders(e.target.value); setShowBuilderResults(true); }}
              onFocus={() => { if (builderResults.length) setShowBuilderResults(true); }}
              onBlur={() => setTimeout(() => setShowBuilderResults(false), 200)}
              placeholder="Find a builder by name or apiary..."
              className="pl-9 h-9 rounded-xl bg-white text-sm"
              data-testid="builder-search-input"
            />
          </div>
          {showBuilderResults && builderResults.length > 0 && (
            <div className="absolute left-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg z-30 w-80 py-1" data-testid="builder-search-results">
              {builderResults.map(b => (
                <button
                  key={b.id}
                  onClick={() => { navigate(`/builder/${b.id}`); setBuilderQuery(''); setShowBuilderResults(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-stone-50 transition"
                >
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">
                    {(b.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-stone-800">{b.name}</div>
                    {b.company_name && <div className="text-xs text-stone-500">{b.company_name}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}
          {showBuilderResults && builderQuery.length >= 2 && builderResults.length === 0 && (
            <div className="absolute left-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg z-30 w-80 px-4 py-3 text-sm text-stone-400">No builders found</div>
          )}
        </div>

        {/* Tabs: Ideas / Challenges */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => switchTab('ideas')} className={`text-sm px-4 py-2 rounded-full font-medium transition ${activeTab === 'ideas' ? 'bg-amber-600 text-white' : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'}`}>
            Ideas
          </button>
          <button onClick={() => switchTab('challenges')} className={`text-sm px-4 py-2 rounded-full font-medium transition relative ${activeTab === 'challenges' ? 'bg-orange-600 text-white' : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'}`}>
            Challenges {challenges.filter(c => c.status === 'active').length > 0 && <span className="ml-1 bg-orange-500 text-white text-[10px] px-1.5 rounded-full">{challenges.filter(c => c.status === 'active').length}</span>}
          </button>
          {notifications.length > 0 && (
            <div className="ml-auto flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
              <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
              {notifications.length} new from builders you follow
            </div>
          )}
        </div>

        {activeTab === 'challenges' ? (
          <ChallengesTab challenges={challenges} user={user} isDemo={isDemo} canPost={canPost} navigate={navigate} fetchChallenges={async () => {
            try {
              const token = localStorage.getItem('token');
              const res = await axios.get(`${API}/diy-challenges`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
              setChallenges(res.data || []);
            } catch { /* empty */ }
          }} />
        ) : (
        <>
        {/* Search + Sort */}
        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ideas..." className="pl-9 h-10 rounded-xl bg-white" data-testid="search-input" />
          </div>
          <Select value={sortBy || 'newest'} onValueChange={v => setSortBy(v === 'newest' ? '' : v)}>
            <SelectTrigger className="h-10 w-40 rounded-xl bg-white text-xs"><SelectValue placeholder="Sort by" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="top_rated">Top Rated</SelectItem>
              <SelectItem value="most_made">Most Made</SelectItem>
              <SelectItem value="most_liked">Most Liked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-5 items-center">
          <button onClick={() => { setCategory(''); setSavedOnly(false); }}
            className={`text-xs px-3 py-1.5 rounded-full font-medium border transition ${!category && !savedOnly ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50'}`}>
            All
          </button>
          {CATEGORIES.map(c => {
            const badge = categoryBadges[c.value];
            return (
              <button key={c.value} onClick={() => {
              const newCat = category === c.value ? '' : c.value;
              setCategory(newCat);
              if (newCat) {
                const token = localStorage.getItem('token');
                if (token) axios.post(`${API}/diy-ideas/category-viewed/${newCat}`, {}, { headers: { Authorization: `Bearer ${token}` } }).then(() => setCategoryBadges(prev => { const n = {...prev}; delete n[newCat]; return n; })).catch(() => {});
              }
            }}
              className={`text-xs px-3 py-1.5 rounded-full font-medium border transition relative ${category === c.value ? CAT_COLORS[c.value] : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50'}`}>
              {c.label}
              {badge > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{badge > 9 ? '9+' : badge}</span>}
            </button>
            );
          })}
          <span className="w-px h-5 bg-stone-200 mx-1" />
          <button onClick={() => setSavedOnly(!savedOnly)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium border transition flex items-center gap-1 ${savedOnly ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50'}`}>
            <Bookmark className={`h-3 w-3 ${savedOnly ? 'fill-current' : ''}`} /> Saved
          </button>
        </div>

        {/* Trending Section */}
        {trending.length > 0 && !search && !category && !savedOnly && (
          <div className="mb-6">
            <h2 className="text-sm font-bold text-stone-700 flex items-center gap-1.5 mb-3">
              <Flame className="h-4 w-4 text-orange-500" /> Trending This Week
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {trending.map(idea => {
                const ph = (idea.photos || [])[0];
                const photoPath = typeof ph === 'string' ? ph : ph?.path || '';
                return (
                  <div key={idea.id} className="flex-shrink-0 w-56 bg-white rounded-xl border border-orange-200 shadow-sm overflow-hidden hover:shadow-md transition cursor-pointer" onClick={() => navigate(`/?view=idea-detail&ideaId=${idea.id}`)}>
                    {photoPath ? (
                      <img src={`${API}/files/${photoPath}`} alt="" className="w-full h-28 object-cover" />
                    ) : (
                      <div className="w-full h-20 bg-gradient-to-br from-orange-50 to-amber-100 flex items-center justify-center"><Flame className="h-6 w-6 text-orange-300" /></div>
                    )}
                    <div className="p-2.5">
                      <h3 className="font-semibold text-xs text-stone-800 truncate">{idea.title}</h3>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-stone-400">
                        <span><Heart className="h-3 w-3 inline" /> {idea.likes || 0}</span>
                        {idea.made_it_count > 0 && <span><Hammer className="h-3 w-3 inline" /> {idea.made_it_count}</span>}
                        {idea.review_avg > 0 && <span><Star className="h-3 w-3 inline text-amber-500 fill-amber-500" /> {idea.review_avg}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Collections Section */}
        {collections.length > 0 && !search && !savedOnly && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-stone-700 flex items-center gap-1.5">
                <FolderPlus className="h-4 w-4 text-indigo-500" /> Collections
              </h2>
              {!isDemo && canPost && (
                <button onClick={() => setShowNewCollection(true)} className="text-xs text-indigo-600 font-medium hover:underline flex items-center gap-1">
                  <Plus className="h-3 w-3" /> New Collection
                </button>
              )}
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {collections.slice(0, 8).map(col => (
                <div key={col.id} onClick={() => navigate(`/?view=collection&collectionId=${col.id}`)} className="flex-shrink-0 w-44 bg-white rounded-xl border border-stone-200 shadow-sm p-3 hover:shadow-md transition cursor-pointer">
                  <h3 className="font-semibold text-xs text-stone-800 truncate">{col.name}</h3>
                  <p className="text-[10px] text-stone-400 mt-0.5 truncate">{col.description || 'No description'}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-indigo-600">{(col.idea_ids || []).length} ideas</span>
                    <ChevronRight className="h-3 w-3 text-stone-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="text-center py-16 text-stone-400">Loading...</div>
        ) : ideas.length === 0 ? (
          <div className="text-center py-16">
            <Image className="h-16 w-16 mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500 text-sm">{search || category || savedOnly ? 'No ideas match your filters' : 'No ideas yet — be the first!'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ideas.map(idea => (
              <IdeaCard key={idea.id} idea={idea} currentUserId={user?.id} onLike={handleLike} onDelete={handleDelete} onEdit={openEdit} isDemo={isDemo} isBookmarked={bookmarkedIds.includes(idea.id)} onBookmark={handleBookmark} onAddToCollection={(id) => setAddToColIdea(id)} />
            ))}
          </div>
        )}
        </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold text-stone-800">{editingIdea ? 'Edit Idea' : 'Share a DIY Idea'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-3">
            <div><Label className="text-stone-700 font-medium">Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Cedar Top Bar Hive Plans" className="mt-1" /></div>
            <div><Label className="text-stone-700 font-medium">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Describe your project, materials, tips..." className="mt-1 min-h-[70px]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-stone-700 font-medium text-xs">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}><SelectTrigger className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-stone-700 font-medium text-xs">Difficulty</Label>
                <Select value={form.difficulty || 'none'} onValueChange={v => setForm({...form, difficulty: v === 'none' ? '' : v})}><SelectTrigger className="mt-1 h-9 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="none">Not set</SelectItem>{Object.entries(DIFF_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-stone-700 font-medium text-xs">Cost</Label>
                <Select value={form.cost_rating || 'none'} onValueChange={v => setForm({...form, cost_rating: v === 'none' ? '' : v})}><SelectTrigger className="mt-1 h-9 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="none">Not set</SelectItem>{Object.entries(COST_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-stone-700 font-medium text-xs">Time</Label>
                <Select value={form.time_estimate || 'none'} onValueChange={v => setForm({...form, time_estimate: v === 'none' ? '' : v})}><SelectTrigger className="mt-1 h-9 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="none">Not set</SelectItem>{Object.entries(TIME_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>

            {/* Photos with captions */}
            <div>
              <Label className="text-stone-700 font-medium">Photos (max 10)</Label>
              <div className="mt-1 space-y-2">
                <div className="flex gap-2 items-center">
                  <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" id="diy-photo-upload" />
                  <label htmlFor="diy-photo-upload" className="cursor-pointer text-xs bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full font-medium transition flex items-center gap-1"><Upload className="h-3.5 w-3.5" /> Upload Photos</label>
                  <span className="text-xs text-stone-400">{uploadedPhotos.length}/10</span>
                  {uploading && <span className="text-xs text-amber-600 animate-pulse">Uploading...</span>}
                </div>
                {uploadedPhotos.map((p, i) => (
                  <div key={i} className="flex gap-2 items-center bg-stone-50 rounded-lg p-2 border border-stone-200">
                    <img src={`${API}/files/${p.path}`} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                    <Input value={p.caption} onChange={e => { const up = [...uploadedPhotos]; up[i] = {...up[i], caption: e.target.value}; setUploadedPhotos(up); }} placeholder={`Caption for photo ${i+1} (optional)`} className="h-8 text-xs flex-1" />
                    <button onClick={() => setUploadedPhotos(prev => prev.filter((_,idx) => idx !== i))} className="text-stone-400 hover:text-red-500 flex-shrink-0"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Plan */}
            <div>
              <Label className="text-stone-700 font-medium">Upload Plans (PDF or Image)</Label>
              <div className="mt-1">
                {!uploadedPlan ? (
                  <div className="flex gap-2 items-center">
                    <input type="file" accept=".pdf,image/*" onChange={handlePlanUpload} className="hidden" id="diy-plan-upload" />
                    <label htmlFor="diy-plan-upload" className="cursor-pointer text-xs bg-stone-700 hover:bg-stone-800 text-white px-4 py-2 rounded-full font-medium transition flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Upload Plan File</label>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-stone-50 rounded-lg px-3 py-2 border border-stone-200">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-stone-700 flex-1 truncate">{uploadedPlan.filename}</span>
                    <button onClick={() => setUploadedPlan(null)} className="text-stone-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
            </div>

            <div><Label className="text-stone-700 font-medium">YouTube Link (optional)</Label><Input value={form.youtube_url} onChange={e => setForm({...form, youtube_url: e.target.value})} placeholder="https://youtube.com/watch?v=..." className="mt-1" /></div>
            <div><Label className="text-stone-700 font-medium">Tags (comma separated)</Label><Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="woodworking, plans, beginner" className="mt-1" /></div>


            {/* Sharing Permission */}
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-stone-700 font-medium text-sm">Allow others to share this idea?</Label>
                  <p className="text-[11px] text-stone-500 mt-0.5">When enabled, a share button appears on your idea for others to post to social media.</p>
                </div>
                <button type="button" onClick={() => setForm({...form, allow_sharing: !form.allow_sharing})}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${form.allow_sharing ? 'bg-green-600 text-white' : 'bg-stone-200 text-stone-600'}`}
                  data-testid="allow-sharing-toggle">
                  {form.allow_sharing ? 'Yes' : 'No'}
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1 rounded-full">Cancel</Button>
              <Button onClick={handleSubmit} disabled={creating} className="flex-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white">{creating ? 'Saving...' : editingIdea ? 'Update' : 'Share Idea'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Collection Dialog */}
      <Dialog open={showNewCollection} onOpenChange={setShowNewCollection}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle>New Collection</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <Input value={newColName} onChange={e => setNewColName(e.target.value)} placeholder="e.g., Winter Prep Builds" />
            <Input value={newColDesc} onChange={e => setNewColDesc(e.target.value)} placeholder="Description (optional)" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowNewCollection(false)} className="flex-1 rounded-full">Cancel</Button>
              <Button onClick={createCollection} disabled={!newColName.trim()} className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white">Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add to Collection Dialog */}
      <Dialog open={!!addToColIdea} onOpenChange={() => setAddToColIdea(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle>Add to Collection</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-3">
            {myCollections.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-sm text-stone-500 mb-3">No collections yet</p>
                <Button onClick={() => { setAddToColIdea(null); setShowNewCollection(true); }} className="rounded-full bg-indigo-600 text-white text-sm">Create Collection</Button>
              </div>
            ) : myCollections.map(col => (
              <button key={col.id} onClick={() => addIdeaToCollection(col.id)} className="w-full flex items-center justify-between p-3 rounded-xl border border-stone-200 hover:bg-stone-50 transition text-left">
                <div>
                  <div className="text-sm font-medium text-stone-800">{col.name}</div>
                  <div className="text-xs text-stone-400">{(col.idea_ids || []).length} ideas</div>
                </div>
                <Plus className="h-4 w-4 text-indigo-600" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DIYIdeas;
